
































import { Component, Watch } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import { namespace } from "vuex-class";
import { Auth } from "@/mixins/auth";
import { Roles } from "@/models/user/roles";
import AuthService from "@/services/auth_service";
import NetSuiteService from "@/services/netsuite_service";
import Header from "@/components/header/Header.vue";
import Sidebar from "@/components/sidebar/Menu.vue";
import CartModal from "@/components/elements/modal/CartModal.vue";
import ConfigCodeModal from "@/components/elements/modal/ConfigCodeModal.vue";
import ImpersonationBanner from "@/components/elements/ImpersonationBanner.vue";
import EditModeBanner from "@/components/elements/EditModeBanner.vue";
import ToastNotifications from "@/components/elements/toast/ToastNotifications.vue";
import { EventBus } from "./event-bus";
import { Mobile } from "@/mixins/mobile";
import { LineItem } from "./models/order/order";
import { User } from "./models/user/user";
import { OrderMixin } from "./mixins/orders/mixin";
import { PartialNetSuiteCustomer, PriceLevel } from "./models/netsuite/netsuite";
import { GlobalActions, GlobalGetters } from "./store/modules/global/types";
import { OrderActions } from "./store/modules/order/types";
import { NotificationService } from "./services/notification_service";
import { prefetchCustomerDetails } from "./services/customer_details_prefetch";
import { StoreModule } from "./store/types";

@Component({
  components: {
    ImpersonationBanner,
    EditModeBanner,
    Header,
    Sidebar,
    CartModal,
    ConfigCodeModal,
    ToastNotifications
  }
})
export default class App extends mixins(Auth, Mobile, OrderMixin) {
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetPriceLevels))
  priceLevels!: PriceLevel[];

  @(namespace(StoreModule.Global).Action(GlobalActions.AddPriceLevels))
  addPriceLevels!: (value: PriceLevel[]) => Promise<void>;

  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetCustomers))
  storeCustomers!: PartialNetSuiteCustomer[];

  @(namespace(StoreModule.Global).Action(GlobalActions.AddCustomers))
  addCustomers!: (value: PartialNetSuiteCustomer[]) => Promise<void>;

  @(namespace(StoreModule.Global).Action(GlobalActions.AddMultiplier))
  addMultiplier!: (value: PriceLevel | null) => Promise<void>;

  @(namespace(StoreModule.Order).Action(OrderActions.ResetForUserChange))
  resetOrderForUserChange!: () => Promise<void>;

  private surchargeValue = process.env.VUE_APP_PRICE_SURCHARGE_VALUE_NUMBER || "";
  private surchargeDate = process.env.VUE_APP_PRICE_SURCHARGE_DATE || "";
  private surchargeDismissed = sessionStorage.getItem("surcharge_banner_dismissed") === "true";

  get hasSurchargeBanner(): boolean {
    return !this.surchargeDismissed && !!this.surchargeValue && !!this.surchargeDate;
  }

  protected environment = process.env.VUE_APP_ENV;
  protected envBuildBranch = process.env.VUE_APP_BUILD_BRANCH;
  protected envBuildDate = process.env.VUE_APP_BUILD_DATE;
  protected Roles = Roles;
  protected authService = new AuthService();
  protected netSuiteService = new NetSuiteService();
  protected showModal = false;
  protected showConfigCodeModal = false;
  protected loader: any = null;
  protected cartInfo: {
    qty?: number;
    model: string;
    productline?: string;
    name?: string;
    finish?: string;
    fabric?: string;
    cost?: number;
    quickshipList?: LineItem[];
    accessoryList?: LineItem[];
    sku?: string;
    // BugHerd #5: the umbrella page adds a base and sometimes a top cover
    // alongside the umbrella, and CartModal describes all of them. Declared
    // here too, or this handler's inline type silently drops the fields.
    baseInfo?: object | null;
    coverInfo?: object | null;
    itemCount?: number;
    // FUPOF-193: an edit's cart index, so View Cart lands on that line.
    cartLine?: number;
  } | null = null;

  async created(): Promise<void> {
    if (this.environment === "development") {
      console.log(
        `Logged Dev App Build Details: Branch - ${
          this.envBuildBranch ? this.envBuildBranch : "N/A"
        } | Date - ${this.envBuildDate ? this.envBuildDate : "N/A"}`
      );
    }
    this.checkAuth();
    EventBus.$on("ssoAuthComplete", () => {
      this.checkAuth();
    });
    EventBus.$on("surchargeBannerDismissed", () => {
      this.surchargeDismissed = true;
    });
    // FUPOF-193: any entry point (umbrella menu, cart, sidebar) asks for the
    // configuration-code modal over the bus; it renders here at the root.
    EventBus.$on("openConfigCodeModal", () => {
      this.showConfigCodeModal = true;
    });
    EventBus.$on(
      "addToCartModal",
      (cartInfo: {
        qty?: number;
        model: string;
        productline?: string;
        name?: string;
        finish?: string;
        fabric?: string;
        options?: object;
        cost?: number;
        quickshipList?: LineItem[];
        accessoryList?: LineItem[];
        sku?: string;
    // BugHerd #5: the umbrella page adds a base and sometimes a top cover
    // alongside the umbrella, and CartModal describes all of them. Declared
    // here too, or this handler's inline type silently drops the fields.
    baseInfo?: object | null;
    coverInfo?: object | null;
    itemCount?: number;
    cartLine?: number;
      }) => {
        this.showModal = true;
        this.cartInfo = cartInfo;
      }
    );

    this.handleView();
    window.addEventListener("resize", this.handleView);
  }

  protected handleView() {
    this.setMobile(window.innerWidth <= 640);
    this.setTablet(window.innerWidth < 960 && window.innerWidth > 640);
  }

  // protected closeModal(): void {
  //   this.showModal = false;
  //   this.editItem();
  // }

  protected viewCart(): void {
    this.showModal = false;
    // FUPOF-193: after an edit, land on the line that was edited (see
    // Cart.focusRequestedLine) rather than wherever the page was scrolled.
    const line = this.cartInfo ? this.cartInfo.cartLine : undefined;
    this.$router.push(
      line !== undefined
        ? { path: "/cart", query: { line: String(line) } }
        : "/cart"
    );
  }

  protected closeConfigCodeModal(): void {
    this.showConfigCodeModal = false;
  }

  protected async checkAuth(): Promise<void> {
    // Skip auth check on callback/login routes — AuthCallback handles token exchange
    const path = this.$route?.path;
    if (path === "/auth/callback" || path === "/login") {
      return;
    }

    // @ts-ignore — accessing store state directly for token
    const token = this.$store.state.global.authToken;
    if (!token) {
      this.setLoggedIn(false);
      return;
    }

    this.showLoader();
    try {
      const cachedUser = this.user;
      const role = this.userRole;
      const isAdmin = role === Roles.Admin;

      if (!isAdmin && !cachedUser?.netsuite_id) {
        this.setLoggedIn(false);
        this.setUserRole(null);
        this.setAuthToken(null);
        this.setUserInfo({} as User);
        await this.authService.logout();
        this.closeLoader();
        this.$router.push("/error");
        return;
      }

      this.setLoggedIn(true);

      // Price levels and the customer list are independent lookups — start both
      // before awaiting either, so the login loader waits on the slower of the
      // two rather than on their sum. Each is skipped (null) when the store
      // already holds it. Both requests are kicked off on their own statement:
      // inside a Promise.all([...]) literal, a throw while building the second
      // element would leave that request unsent while the first still went out.
      const priceLevelsRequest: Promise<PriceLevel[] | null> = this.priceLevels
        ?.length
        ? Promise.resolve(null)
        : this.netSuiteService.getPriceLevels();

      let customersRequest: Promise<PartialNetSuiteCustomer[] | null> =
        Promise.resolve(null);
      if (!this.storeCustomers?.length) {
        if (isAdmin) {
          customersRequest = this.netSuiteService.getPartialNetSuiteCustomers();
        } else if (cachedUser?.netsuite_id) {
          customersRequest = this.netSuiteService.getMyCustomers();
        } else {
          customersRequest = Promise.resolve([]);
        }
      }

      const [priceLevels, customers] = await Promise.all([
        priceLevelsRequest,
        customersRequest
      ]);

      // A 401 on either lookup means the token is dead: the api.ts interceptor
      // has already dropped it and is redirecting to login. Both lookups swallow
      // errors and return [], so carrying on would store empty lists and — with
      // no customers — discard the cart as if its customer had been revoked.
      // @ts-ignore — accessing store state directly for token
      if (!this.$store.state.global.authToken) {
        this.closeLoader();
        return;
      }

      if (priceLevels) await this.addPriceLevels(priceLevels);
      if (customers) await this.addCustomers(customers);
      await this.discardInvalidCustomer();
      this.closeLoader();
    } catch (err) {
      console.error("checkAuth error:", err);
      this.setLoggedIn(false);
      this.setUserRole(null);
      this.setAuthToken(null);
      this.setUserInfo({} as User);
      this.$router.push("/login");
    }
  }

  /**
   * FUPOF-191 (BIGGER-TWEAKS #2): the selected customer is persisted to
   * localStorage, so it survives a session. If it isn't in the current user's
   * customer list — a different user signed in, or this rep lost access to the
   * customer — the whole order state goes with it: the cart's lines are priced
   * off that customer's price level and `draft_id` may point at someone else's
   * quote. The PSQL draft is untouched; it stays in its owner's Saved Quotes.
   *
   * The "New Customer" pseudo-customer has no `customer_netsuite_id` and so is
   * never in the list; it is exempt here and governed by AuthCallback's
   * user-change check instead.
   */
  protected async discardInvalidCustomer(): Promise<void> {
    const nsId = this.mixinStoreOrder?.customer_netsuite_id;
    if (!nsId) return;
    if (this.storeCustomers?.some(c => c.customer_id === nsId)) {
      // FUPOF-191: the customer was picked in an earlier session, so
      // assignCustomer's prefetch never ran for this page load. Warm it here so
      // a reload followed by a walk to checkout is just as quick.
      prefetchCustomerDetails(nsId);
      return;
    }
    await this.resetOrderForUserChange();
    await this.addMultiplier(null);
    NotificationService.show(
      "Your previously selected customer is no longer available. Your cart has been cleared.",
      "warning"
    );
  }

  protected showLoader(): void {
    this.loader = this.$loading.show(
      {
        opacity: 1
      },
      {
        before: this.$createElement("h1", {
          domProps: { innerHTML: "Authenticating user..." }
        })
      }
    );
  }

  protected closeLoader(): void {
    setTimeout(() => {
      this.loader.hide();
    }, 300);
  }

  beforeDestroy(): void {
    EventBus.$off("addToCartModal");
    EventBus.$off("openConfigCodeModal");
    EventBus.$off("surchargeBannerDismissed");
  }
}
